import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Play, Film } from "lucide-react";
import { SectionHead } from "@/components/SectionHead";
import { Reveal } from "@/components/Reveal";
import { useContent } from "@/context/ContentContext";
import { formatPrice } from "@/lib/api";

function VideoCard({ video, products }) {
  const [playingId, setPlayingId] = useState(null);
  const related = products.filter((p) => !p.shop_hidden && video.related_product_ids?.includes(p.id));

  return (
    <article data-testid={`video-card-${video.id}`} className="group border border-[#1A1A22] bg-[#0F0F14] hover:border-[#F26522]/60 transition-colors">
      <div className="relative aspect-video overflow-hidden bg-[#121218]">
        {video.youtube_id && playingId ? (
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${video.youtube_id}?autoplay=1&rel=0`}
            title={video.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        ) : (
          <button
            data-testid={`video-play-${video.id}`}
            onClick={() => video.youtube_id && setPlayingId(video.id)}
            aria-label={video.youtube_id ? `Play ${video.title}` : `${video.title} — premiere coming soon`}
            className="absolute inset-0 w-full h-full"
          >
            <img
              src={video.poster}
              alt={`Poster frame for ${video.title}`}
              loading="lazy"
              className="w-full h-full object-cover saturate-[0.9] contrast-[1.05] group-hover:scale-[1.03] transition-transform duration-700"
            />
            <span className="absolute inset-0 bg-gradient-to-t from-[#08080A] via-transparent to-[#08080A]/30" aria-hidden="true" />
            {video.youtube_id ? (
              <span className="absolute inset-0 flex items-center justify-center">
                <span className="w-20 h-20 rounded-full bg-[#F26522] text-black flex items-center justify-center shadow-[0_0_50px_rgba(242,101,34,0.45)] transition-transform group-hover:scale-110">
                  <Play className="w-9 h-9 ml-1" aria-hidden="true" />
                </span>
              </span>
            ) : (
              <span className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                <Film className="w-10 h-10 text-[#F26522]" aria-hidden="true" />
                <span className="font-mono text-sm font-semibold tracking-[0.3em] uppercase text-[#F5E6D3] bg-[#08080A]/85 px-4 py-2 border border-[#2A2A36]">
                  Premiere Soon
                </span>
              </span>
            )}
          </button>
        )}
      </div>
      <div className="p-5 sm:p-6 flex flex-col gap-2">
        <p className="font-mono text-[11px] tracking-[0.25em] uppercase text-[#F26522]">{video.meta}</p>
        <h3 className="font-display font-extrabold uppercase text-xl text-white leading-tight">{video.title}</h3>
        <p className="text-zinc-300 text-sm">{video.description}</p>
        {related.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-2">
            {related.map((p) => (
              <Link
                key={p.id}
                to="/shop"
                className="border border-[#2A2A36] hover:border-[#F26522] px-3 py-2 font-mono text-xs uppercase tracking-[0.15em] text-[#F5E6D3] hover:text-[#F26522] transition-colors"
              >
                Worn in video: {p.name} — {formatPrice(p.price, p.currency)}
              </Link>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

/** Cinematic film gallery — one large video per row on mobile. */
export default function VideoList({ limit }) {
  const { videos, products } = useContent();
  const list = limit ? videos.slice(0, limit) : videos;
  if (!list.length) return null;

  return (
    <div>
      <SectionHead
        eyebrow={limit ? "Visuals" : "The Film Catalog"}
        title={limit ? "Music Videos & Films" : "Cinematic Visuals"}
        index="/ 03"
      />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 lg:gap-10">
        {list.map((v, i) => (
          <Reveal key={v.id} delay={i * 0.08} className={i === 0 && list.length > 1 && !limit ? "md:col-span-2" : ""}>
            <VideoCard video={v} products={products} />
          </Reveal>
        ))}
      </div>
    </div>
  );
}
